import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Briefcase, Calendar, Award } from 'lucide-react';
import Marquee from 'react-fast-marquee';

const Home = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 12,
    hours: 7,
    minutes: 45,
    seconds: 30
  });

  useEffect(() => {
    const targetDate = new Date('2026-06-29T23:59:59');
    
    const timer = setInterval(() => {
      const now = new Date();
      const difference = targetDate - now;
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const stats = [
    { icon: Users, number: '100+', label: 'MEMBERS' },
    { icon: Briefcase, number: '20+', label: 'STARTUPS' },
    { icon: Calendar, number: '25+', label: 'EVENTS' },
    { icon: Award, number: '10+', label: 'INDUSTRY MENTORS' }
  ];

  return (
    <div data-testid="home-page">
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_karobar-hub/artifacts/l4i7elt4_WhatsApp%20Image%202026-06-04%20at%209.29.02%20PM.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-black/60"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32 text-left">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <div className="mb-8">
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-gold-500 mb-4">THE OFFICIAL ENTREPRENEURSHIP CLUB OF CHRIST UNIVERSITY</p>
                <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-tighter uppercase leading-none mb-6" style={{ fontFamily: 'Open Sans' }}>
                  BUILD.<br />
                  PITCH.<br />
                  <span className="text-gradient">LEAD.</span>
                </h1>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <p className="text-gray-300 text-lg leading-relaxed">IDEAS</p>
                <p className="text-gray-300 text-lg leading-relaxed">INNOVATION</p>
                <p className="text-gray-300 text-lg leading-relaxed">IMPACT</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/join-us" className="btn-primary" data-testid="hero-join-club-button">
                  JOIN THE CLUB
                </Link>
                <Link to="/events" className="glass-card px-8 py-4 text-white font-semibold hover:text-gold-500 transition-colors flex items-center justify-center space-x-2" data-testid="hero-explore-events-button">
                  <span>EXPLORE EVENTS</span>
                  <ArrowRight size={20} />
                </Link>
              </div>
            </div>

            <div className="glass-card p-8 animate-fade-in" data-testid="upcoming-event-card">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-gold-500 mb-3">UPCOMING EVENT</p>
              <h2 className="text-4xl font-black mb-6" style={{ fontFamily: 'Open Sans' }}>ONE 2 KA FOUR 2026</h2>
              <p className="text-gray-400 mb-8">THE ULTIMATE BUSINESS CONCLAVE</p>
              
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="text-center">
                  <div className="text-4xl font-black countdown-timer">{timeLeft.days}</div>
                  <div className="text-xs text-gray-400 tracking-widest uppercase mt-1">DAYS</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black countdown-timer">{timeLeft.hours}</div>
                  <div className="text-xs text-gray-400 tracking-widest uppercase mt-1">HOURS</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black countdown-timer">{timeLeft.minutes}</div>
                  <div className="text-xs text-gray-400 tracking-widest uppercase mt-1">MINUTES</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black countdown-timer">{timeLeft.seconds}</div>
                  <div className="text-xs text-gray-400 tracking-widest uppercase mt-1">SECONDS</div>
                </div>
              </div>
              
              <button className="btn-primary w-full" data-testid="register-now-button">REGISTER NOW</button>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-gradient-to-b from-black to-[#121212]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="glass-card p-8 text-center hover:scale-105 transition-transform" data-testid={`stat-card-${stat.label.toLowerCase().replace(' ', '-')}`}>
                  <Icon className="w-12 h-12 text-gold-500 mx-auto mb-4" />
                  <div className="text-5xl font-black text-gold-500 mb-2" style={{ fontFamily: 'Open Sans' }}>{stat.number}</div>
                  <div className="text-xs tracking-widest text-gray-400 uppercase">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-padding bg-[#121212]" data-testid="sponsors-section">
        <div className="max-w-7xl mx-auto mb-12">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-center mb-4" style={{ fontFamily: 'Open Sans' }}>OUR PARTNERS</h2>
          <p className="text-center text-gray-400 text-lg">Trusted by industry leaders and innovators</p>
        </div>
        
        <Marquee gradient={false} speed={50} className="py-8">
          <div className="flex items-center space-x-16 px-8">
            <img src="https://customer-assets.emergentagent.com/job_karobar-hub/artifacts/a7f8x1ly_image-removebg-preview%20%2812%29.png" alt="Bombay Belly & Co" className="h-24 opacity-70 hover:opacity-100 transition-opacity" />
            <img src="https://customer-assets.emergentagent.com/job_karobar-hub/artifacts/cyk2kpgw_image-removebg-preview%20%2813%29.png" alt="Partner 2" className="h-24 opacity-70 hover:opacity-100 transition-opacity" />
            <img src="https://customer-assets.emergentagent.com/job_karobar-hub/artifacts/z1ixmrlt_merkuri_fashion-removebg-preview.png" alt="Merkuri Fashion" className="h-24 opacity-70 hover:opacity-100 transition-opacity" />
            <img src="https://customer-assets.emergentagent.com/job_karobar-hub/artifacts/5yu6aozs_image-removebg-preview%20%2814%29.png" alt="Microport" className="h-24 opacity-70 hover:opacity-100 transition-opacity" />
            <img src="https://customer-assets.emergentagent.com/job_karobar-hub/artifacts/60e4bube_image-removebg-preview%20%2815%29.png" alt="Mayukh" className="h-24 opacity-70 hover:opacity-100 transition-opacity" />
            <img src="https://customer-assets.emergentagent.com/job_karobar-hub/artifacts/e2ybk2xu_image-removebg-preview%20%287%29.png" alt="AMR" className="h-24 opacity-70 hover:opacity-100 transition-opacity" />
            <img src="https://customer-assets.emergentagent.com/job_karobar-hub/artifacts/vul1lifx_image-removebg-preview%20%288%29.png" alt="Motor Company" className="h-24 opacity-70 hover:opacity-100 transition-opacity" />
            <img src="https://customer-assets.emergentagent.com/job_karobar-hub/artifacts/deqncvnu_image-removebg-preview%20%289%29.png" alt="Ganga Bakery & Sweets" className="h-24 opacity-70 hover:opacity-100 transition-opacity" />
            <img src="https://customer-assets.emergentagent.com/job_karobar-hub/artifacts/yps6lele_image-removebg-preview%20%2810%29.png" alt="Suzuki" className="h-24 opacity-70 hover:opacity-100 transition-opacity" />
            <img src="https://customer-assets.emergentagent.com/job_karobar-hub/artifacts/iybri1j8_image-removebg-preview%20%2811%29.png" alt="MYOP" className="h-24 opacity-70 hover:opacity-100 transition-opacity" />
            <img src="https://customer-assets.emergentagent.com/job_karobar-hub/artifacts/zyq7e3wk_image-removebg-preview%20%281%29.png" alt="MYOP 2" className="h-24 opacity-70 hover:opacity-100 transition-opacity" />
            <img src="https://customer-assets.emergentagent.com/job_karobar-hub/artifacts/8t2a6w8c_image-removebg-preview%20%282%29.png" alt="GameX" className="h-24 opacity-70 hover:opacity-100 transition-opacity" />
          </div>
        </Marquee>
      </section>

      <section className="section-padding bg-gradient-to-b from-[#121212] to-black" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6" style={{ fontFamily: 'Open Sans' }}>READY TO MAKE AN IMPACT?</h2>
          <p className="text-gray-400 text-lg mb-8 leading-relaxed">Join KAROBAR and be part of a community that turns ideas into reality. Connect with mentors, build your startup, and lead the entrepreneurial revolution.</p>
          <Link to="/join-us" className="btn-primary inline-block" data-testid="cta-join-button">JOIN NOW</Link>
        </div>
      </section>
    </div>
  );
};

export default Home;